import type { DeploymentList } from "../../types/generated/api/DeploymentList"
import { ProgramItem } from "../../types/generated/api/ProgramItem"
import type { ScoreCardSetList } from "../../types/generated/api/ScoreCardSetList"

/**
 * Returns a list of items that are not selected in other program cards,
 * as well as the item at the current index (even if selected).
 *
 * This function is used to determine which items (either `ScoreCardSetList` or `DeploymentList`)
 * are available to be selected in a specific program card by filtering out the items already
 * selected in other cards, except for the one at the given index.
 *
 * @param currentIndex - The index of the currently active program item (card).
 * @param items - A list of all possible items that can be selected (`ScoreCardSetList[]` or `DeploymentList[]`).
 * @param usedItems - A list of items that are currently selected in the program, where each item may reference a scorecard or deployment.
 *
 * @returns A list of items available for selection, each with a `uuid`, `name`, and `hidden` flag set to false.
 */
export const getNonSelectedItems = (
  currentIndex: number,
  items: ScoreCardSetList[] | DeploymentList[],
  usedItems: ProgramItem[],
): { uuid: string; name: string; hidden: boolean }[] => {
  const dataList = items

  return (
    dataList
      ?.filter((item) => {
        // Only include the current item and items not selected in other cards
        return usedItems.every((formItem, index) => {
          if (index === currentIndex) return true

          if (formItem.item_type === ProgramItem.item_type.SCORECARDSET)
            return formItem.scorecard_set?.uuid !== item.uuid
          else return formItem.project?.uuid !== item.uuid
        })
      })
      .map((item) => ({ uuid: item.uuid, name: item.name, hidden: false })) || []
  )
}
